import { Button, Paper, rem, Tabs, Text } from "@mantine/core";
import { IconArrowLeft, IconCheck } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { LichessResponse } from "../getOpeningBookMove";
import MoveStatistics from "./MoveStatistics";
import { useExclusionsStorage, usePersistedStorage, useStorage } from "../ZustandState";
import _ from "lodash";
import { notifications } from "@mantine/notifications";
import { Exclusion, Position } from "../models";
import { postExclusions } from "../backendEndpoints";
import { Chess } from "chess.js";
import { useQueryClient } from "react-query";
import ExclusionsTable from "./ExclusionsTable";

interface Props {
    openingData?: LichessResponse;
    isPlayersTurn: boolean;
    selectedPosition: Position;
    game: Chess;
    exclusions?: Exclusion[];
    onLoadFen: (fen: string) => void;
}

const PositionsSidePanel: React.FC<Props> = ({
    openingData,
    isPlayersTurn,
    selectedPosition,
    game,
    exclusions,
    onLoadFen,
}) => {
    const navigate = useNavigate();
    const setExcludedMoves = useExclusionsStorage((state) => state.setExcludedMoves);
    const excludedMoves = useExclusionsStorage((state) => state.excludedMoves);
    const queryClient = useQueryClient();
    const setSelectedPosition = usePersistedStorage((state) => state.setSelectedPosition);

    const hasExcludedAllMoves = openingData?.moves.every((move) => excludedMoves.includes(move.san));

    const handleOnSave = async () => {
        const movesWithProbabilities = excludedMoves.map((move) => ({ move, probability: 0 }));
        await postExclusions(selectedPosition.id!, movesWithProbabilities, game.fen());
        queryClient.invalidateQueries({ queryKey: ["exclusions"] });
        notifications.show({
            title: "Changes saved",
            message: "",
            color: "green",
            icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
        });
        // Clean cache so that the new exclusions are fetched
        setSelectedPosition(undefined);
    };

    const alreadyExcludedMoves = exclusions
        ?.filter((exclusion) => exclusion.fen === game?.fen())
        .map((exclusion) => exclusion.move);

    const exclusionsByFen = _.groupBy(exclusions, "fen");

    return (
        <div className="bg-[#25262b] w-[400px] [@media(max-width:1023px)]:w-full px-6 py-4 text-left">
            <div className="w-full flex justify-start items-center">
                <Button
                    leftSection={<IconArrowLeft />}
                    component="a"
                    variant="subtle"
                    onClick={() => navigate("/positions")}
                >
                    Back
                </Button>
            </div>
            <Tabs mt="xl" defaultValue="exclude">
                <Tabs.List>
                    <Tabs.Tab value="exclude">
                        <Text>Moves</Text>
                    </Tabs.Tab>
                    <Tabs.Tab value="exclusions">
                        {_.isEmpty(exclusions) && <Text>Your exclusions</Text>}
                        {!_.isEmpty(exclusions) && <Text>Your exclusions ({_.keys(exclusionsByFen).length})</Text>}
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="exclude">
                    <Paper p="md">
                        <Text c="dimmed" size="sm" mb="sm">
                            {isPlayersTurn ? "Make a move on the board" : "Opponent will pick among these moves:"}
                        </Text>
                        {openingData && <MoveStatistics openingData={openingData} isPlayersTurn={isPlayersTurn} />}
                        <Button
                            variant="subtle"
                            disabled={isPlayersTurn}
                            color="blue"
                            className="mt-6"
                            onClick={() => setExcludedMoves([])}
                        >
                            Select all
                        </Button>
                        <Button
                            variant="subtle"
                            color="blue"
                            className="mt-6 ml-2"
                            disabled={isPlayersTurn}
                            onClick={() => setExcludedMoves(openingData?.moves.map((move) => move.san) || [])}
                        >
                            Select none
                        </Button>
                    </Paper>
                    {!hasExcludedAllMoves &&
                        !isPlayersTurn &&
                        !_.isEqual(excludedMoves.sort(), alreadyExcludedMoves?.sort()) && (
                            <Button className="mt-12 w-full" color="green" onClick={handleOnSave}>
                                Save changes
                            </Button>
                        )}
                </Tabs.Panel>
                <Tabs.Panel value="exclusions">
                    {_.isEmpty(exclusions) && (
                        <Text c="dimmed" fs="italic" mt="xl">
                            You have no excluded moves
                        </Text>
                    )}
                    {!_.isEmpty(exclusions) && (
                        <Paper p="md">
                            <ExclusionsTable exclusionsByFen={exclusionsByFen} game={game} onLoadFen={onLoadFen} />
                        </Paper>
                    )}
                </Tabs.Panel>
            </Tabs>
        </div>
    );
};

export default PositionsSidePanel;
